import JSZip from "jszip";
import { assert, fetchText, generateTimestamp, makeSimulationSchema, msg, MyError, sanitizeFilename, SimulationSchema } from "webgpu-sims";
import type { ArticleData } from "./firebaseTasks";

export function splitContentText(contentText : string) : [string, string, SimulationSchema]{
    const lines = contentText.replaceAll("\r", "").split("\n");
    const startJsonet = lines.findIndex(x => x.startsWith("<!-- START OF SCHEMA."));
    const endJsonet = lines.findIndex((x, i) => startJsonet < i && x == "```");
    const startWgsl   = lines.findIndex(x => x.startsWith("<!-- START OF WGSL."));

    assert(startJsonet != -1 && startJsonet < endJsonet && endJsonet < startWgsl);
    assert(lines[startJsonet + 1] == "```jsonet")

    const jsonText = lines.slice(startJsonet + 2, endJsonet).join("\n");
    const markdownText = lines.slice(0, startJsonet).join("\n");

    const schema = makeSimulationSchema(jsonText);

    let idx = startWgsl + 1;
    while(idx < lines.length){
        let line = lines[idx];
        if(line.trim() == ""){
            idx++;
            continue;
        }

        let nodeId = "";
        if(line.startsWith("SHADER:")){
            nodeId = line.substring("SHADER:".length);            
            idx++;
        }
        else{
            const computeNodes = schema.computeNodes();
            assert(computeNodes.length == 1);
            nodeId = computeNodes[0].id;
        }

        assert(idx < lines.length && lines[idx] == "```wgsl");
        idx++;

        let codes = "";
        for(; idx < lines.length; idx++){
            let line = lines[idx];
            if(line == "```"){

                const node = schema.nodeMap.get(nodeId)!;
                assert(node != undefined);
                node.nodeShaderCode = codes;

                idx++;
                break;
            }

            codes += line + "\n";                     
        }
    }

    // msg(`content-Text:[\n${contentText}]`);
    return [markdownText, jsonText, schema];
}

/**
 * Creates a Zip file containing text and an image, then triggers a download.
 * * @param imageUrl The URL of the image you want to download and include.
 * @param textContent The string content you want to save as a text file.
 * @param zipFilename The name of the downloaded zip file (without extension).
 */
export async function downloadArticlesAsZip(articles: ArticleData[]): Promise<void> {
    try {
        // 1. Initialize JSZip
        const zip = new JSZip();

        for(const article of articles){
            if(!article.title.includes("fermion")){
                // continue;
            }
            const authorId = sanitizeFilename(article.authorId);
            const authorFolder = zip.folder(authorId)!;
            assert(authorFolder != null);

            const title = sanitizeFilename(article.title);
            const articleFolder = authorFolder.folder(title)!;
            assert(articleFolder != null);

            const contentText = await fetchText(article.contentFileUrl);
            const [markdownText, schemaText, schema] = splitContentText(contentText);

            const articleObj : any = {
                id     : article.id,
                title  : article.title,
                author : article.authorId,
            };
            if(article.ai != undefined){
                articleObj["ai"] = article.ai;
            }
            const articleText = JSON.stringify(articleObj, null, 4);

            articleFolder.file("article.json", articleText);
            articleFolder.file("markdown.md", markdownText);
            articleFolder.file("schema.js", schemaText);
            for(const [nodeId, node] of schema.nodeMap.entries()){
                if(node.nodeShaderCode != undefined){
                    articleFolder.file(`${nodeId}.wgsl`, node.nodeShaderCode);
                }
            }

            if(article.thumbnailUrl != undefined){

                const response = await fetch(article.thumbnailUrl);
                if (!response.ok) {
                    throw new MyError(`Failed to fetch file: ${response.statusText}\n${article.title} ${article.thumbnailUrl}`);
                }

                const blob = await response.blob();
                articleFolder.file("thumbnail.png", blob);
            }

            msg(`zip:${article.authorId} ${article.title}`);
        }

        // 5. Generate the zip file asynchronously as a Blob
        const zipBlob = await zip.generateAsync({
            type: "blob",
            compression: "DEFLATE", // This applies the standard zip compression
            compressionOptions: {
                level: 6 // Compression level (1-9)
            }
        });

        // 6. Trigger the browser download
        const zipFilename = `gpusims-${generateTimestamp()}.zip`
        triggerDownload(zipBlob, zipFilename);

    } catch (error) {
        throw new MyError(`Error creating zip file:${error}`);
    }
}

/**
 * Helper function to trigger a file download in the browser natively.
 */
function triggerDownload(blob: Blob, filename: string): void {
    // Create a temporary object URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create an invisible anchor element
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;

    // Append to body, click it, and clean up
    document.body.appendChild(link);
    link.click();

    // Clean up the DOM and release the object URL memory
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}